import React, { useState, useRef, useEffect, useMemo } from 'react'
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { BiImageAdd } from "react-icons/bi";
import CountrySelect from './CountrySelect';
import CheckboxSelect from './CheckboxSelect';
import { BiFile } from "react-icons/bi";
import uuid from 'react-uuid';
import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { createTheme, ThemeProvider } from "@mui/material/styles";
import UrlListEditor from './UrlListEditor';
import ImageGalleryUploader from './ImageUploadGallery';
import JoditEditor from 'jodit-react';

const theme = createTheme({
  typography: {
    fontFamily: ["Radio Canada Big", "Arial"].join(",")
  }
});

const AddProject = (props) => {
    const [ projectId, setProjectId ] = useState(null);

    const [ projectName, setProjectName ] = useState("");
    const [ projectOrganisation, setProjectOrganisation ] = useState(null);
    const [ otherOrganisation, setOtherOrganisation ] = useState("");
    const [ projectLocation, setProjectLocation ] = useState(null);
    const [ projectDescription, setProjectDescription ] = useState("");
    const [ projectLatitude, setProjectLatitude ] = useState("");
    const [ projectLongitude, setProjectLongitude ] = useState("");
    const [ thumbnailUrl, setThumbnailUrl ] = useState([]);
    const [ imageUrls, setImageUrls ] = useState([]);
    const [ safetValue, setSafetValue ] = useState([]);
    const [ techValue, setTechValue ] = useState([]);
    const [ stageValue, setStageValue ] = useState(null);
    const [ impactValue, setImpactValue ] = useState(null);
    const [ sdgValue, setSdgValue ] = useState([]);
    const [ outcomeValue, setOutcomeValue ] = useState([]);
    const [ speciesValue, setSpeciesValue ] = useState([]);
    const [ providerValue, setProviderValue ] = useState([]);
    const [ projectStart, setProjectStart ] = useState(dayjs('2025-01-01'));
    const [ projectEnd, setProjectEnd ] = useState(dayjs('2026-01-01'));
    const [ website, setWebsite ] = useState("");
    const [ documents, setDocuments ] = useState([]);
    const [ errorText, setErrorText ] = useState(null);
    const [ successText, setSuccessText ] = useState(null);

    const [ organisations, setOrganisations ] = useState([]);
    const [ techProviders, setTechProviders ] = useState([]);

    const editor = useRef(null);

    const editorConfig = useMemo(() => ({
			readonly: false,
            buttons: ['bold','italic','underline','ul','ol','undo','redo'],
            statusbar: false,
            defaultActionOnPaste: 'insert_as_text',
            askBeforePasteFromWord: false,
            pasteHTML: '',
            pastePlain: true,
			placeholder: 'Project Description'
		}),
		[]
	);

    const handleSubmit = async (event) => {
        event.preventDefault();

        setErrorText(null);
        setSuccessText(null);

        var theOrganisation = projectOrganisation;

        if (theOrganisation === null) {
            setErrorText("Please select a lead organisation");
            return;
        }

        if (projectOrganisation && projectOrganisation.id === -1) {
            if (!otherOrganisation || otherOrganisation.trim().length === 0) {
                setErrorText("Please specify the 'Other' organisation name");
                return;
            }
            console.log("Adding new organisation: " + otherOrganisation);
            const { data, error } = await props.db.schema('api').from('organisations').insert({name: otherOrganisation}).select();
            if (error) {
                console.log(error);
                setErrorText("Error saving organisation. Error code is " + error.code);
                return;
            }
            theOrganisation = {id: data[0].id, name: otherOrganisation};

            loadOrganisations();
            setProjectOrganisation(null);
        }

        const row = {
            project_name: projectName,
            project_description: projectDescription,
            project_country: projectLocation ? projectLocation.name : null,
            latitude: projectLatitude,
            longitude: projectLongitude,
            safet_theme: safetValue.map((i) => (i.label)),
            technology_types: techValue.map((i) => (i.label)),
            project_stage: stageValue ? stageValue.label : null,
            project_impact: impactValue ? impactValue.label : null,
            started: projectStart.format('YYYY-MM-DD'),
            ended: projectEnd.format('YYYY-MM-DD'),
            owner: theOrganisation ? theOrganisation.id : null,
            website_link: website,
            outcomes: outcomeValue.map((i) => (i.label)),
            species: speciesValue.map((i) => (i.label)),
            sdgs: sdgValue.map((i) => (i.number)),
            tech_providers: providerValue ? providerValue.map((i) => (i.id)) : [],
            thumbnail_url: thumbnailUrl.length > 0 ? thumbnailUrl[0] : null,
            mainimage_url: imageUrls.length > 0 ? imageUrls[0] : null,
            image_urls: imageUrls,
            documents: documents,
        };

        console.log(row);

        const { error } = await props.db.schema('api').from('projects').insert(row);
        if (error) {
            console.log(error);
            setErrorText("Error saving project.  Error code is " + error.code);
            return;
        }
        setProjectId(uuid());
        setProjectName("");
        setProjectOrganisation(null);
        setProjectLocation(null);
        setProjectDescription("");
        setProjectLatitude("");
        setProjectLongitude("");
        setThumbnailUrl([]);
        setImageUrls([]);
        setSafetValue([]);
        setTechValue([]);
        setStageValue(null);
        setImpactValue(null);
        setSdgValue([]);
        setOutcomeValue([]);
        setSpeciesValue([]);
        setProviderValue([]);
        setProjectStart(dayjs('2025-01-01'));
        setProjectEnd(dayjs('2026-01-01'));
        setWebsite("");
        setDocuments([]);

        console.log(`Saved project ${row.project_name}`);

        setSuccessText(`Project "${row.project_name}" saved successfully.`);
    }

    const doThumbUpload = async (imgFile, filetype) => {
        return await doUpload("thumb", imgFile, filetype);
    }

    const doImagesUpload = async (imgFile, filetype) => {
        return await doUpload("main", imgFile, filetype);
    }

    const doUpload = async (prefix, imgFile, filetype) => {
        var theUrl = await props.db.storage
            .from('project-images')
            .upload(`${prefix}_${projectId}_${imgFile.name}`, imgFile, {
              contentType: filetype,
        })
        .then((result) => {
            const {data} = props.db.storage.from('project-images').getPublicUrl(result.data.path);
            return data.publicUrl;
        })
        .catch((error) => {
            console.log(error);
            throw error;
        });

        return theUrl;
    }

    useEffect(() => {
        if (!projectId) {
            setProjectId(uuid());
        }
        loadOrganisations();
        loadTechProviders();
    }, []);
    
    const loadOrganisations = async () => {
        let { data } = await props.db
            .schema('api')
			.from('organisations')
			.select('*');
        data.push({id: -1, name: 'Other'});
        setOrganisations(data.map((i) => ({...i, label: i.name})) || []);
    };

    const loadTechProviders = async () => {
        let { data } = await props.db
            .schema('api')
			.from('tech_providers')
			.select('id, name');
        setTechProviders(data.map((i) => ({...i, label: i.name})) || []);
    };

    return (
<ThemeProvider theme={theme}>
<LocalizationProvider dateAdapter={AdapterDayjs}>
    <Card sx={{ maxWidth: "80vw", margin: "auto", marginTop: "50px" }}>
        <CardContent>
            <Typography variant="h4">
                Enter all of the details for your project.
            </Typography>
            <Typography variant="div">
                <p>
                SEA-TECH-IN-MOTION is a map-based tool that shows where technology has been successfully used to help seafood and fisheries become
                more sustainable. Ideally each use of the technology should be represented as a distinct project, with information as granular
                and relevant to the individual use as possible. When selecting options for the theme, technologies, outcomes, or SDGs, please
                consider the specific use case of the project you are entering, rather than the for the overarching technology.
                Please set the project start and end dates to reflect the period when the technology is/was actively deployed
                as part of this project. Species is optional, only select if directly relevant to the project otherwise leave blank.
                This will help users and funders find specific instances that are closest to their own needs.
                </p>
                <p>
                    If the technology provider you are looking for is not listed, please contact <a href="mailto:info@safet.fish">info@safet.fish</a>.
                    If there isn't a specific provider, leave the field blank.
                </p>
                <p>
                Project name, lead organisation, geography, GPS position, project stage, and project impact are required fields. The project will not save if these are not completed.
                </p>
            </Typography>
            <Box component={"form"} id="theForm" onSubmit={handleSubmit}>
                <TextField id="projectName" value={projectName} onChange={(event) => setProjectName(event.target.value)} label="Your Project Name" variant="standard" sx={{width: "100%"}} />  
                <p/>          
                <CheckboxSelect multiple={false} options={organisations} value={projectOrganisation} setValue={setProjectOrganisation} otherValue={otherOrganisation} setOtherValue={setOtherOrganisation} id={"projectOrganisation"} name={"Lead Organisation"}/>
                <p/>
                <CountrySelect value={projectLocation} setValue={setProjectLocation} />
                <p/>
                <Typography>
                    <p>
                    As SEA-TECH-IN-MOTION is a map-based tool, we need to know where to put the pin for this project.
                    If you don't have an exact location, select an approximate location to indicate the country or region.
                    You can find a GPS position using Google Maps by right-clicking on the location.
                    </p>
                    <p>
                    GPS position for map pin:
                    </p>
                </Typography>
                <TextField id="projectLatitude" value={projectLatitude} onChange={(event) => setProjectLatitude(event.target.value)} label="Latitude" variant="standard" sx={{width: 100, marginRight: 2}} />  
                <TextField id="projectLongitude" value={projectLongitude} onChange={(event) => setProjectLongitude(event.target.value)} label="Longitude" variant="standard" sx={{width: 100}} />  
                <p/>
                <JoditEditor
                    ref={editor}
                    value={projectDescription}
                    config={editorConfig}
                    tabIndex={6} 
                    onBlur={newContent => setProjectDescription(newContent)} 
                    onChange={newContent => {}}
                />
                {/* <TextField id="projectDescription" value={projectDescription} onChange={(event) => setProjectDescription(event.target.value)} label="Project Description" variant="standard" multiline rows={4} sx={{width: "100%"}} />   */}
                <p/>
                <DatePicker sx={{marginRight: 1}} label="Start Date" value={projectStart} onChange={(val) => setProjectStart(val)} />
                <DatePicker label="End Date" value={projectEnd} onChange={(val) => setProjectEnd(val)} />
                <p/>
                <CheckboxSelect options={safetOptions} value={safetValue} setValue={setSafetValue} id={"safetTheme"} name={"SAFET Theme"}/>
                <p/>
                <CheckboxSelect options={techOptions} value={techValue} setValue={setTechValue} id={"techTypes"} name={"Technologies Deployed"}/>
                <p/>
                <CheckboxSelect multiple={false} options={stageOptions} value={stageValue} setValue={setStageValue} id={"projectStage"} name={"Project Stage"}/>
                <p/>
                <CheckboxSelect multiple={false} options={impactOptions} value={impactValue} setValue={setImpactValue} id={"projectImpact"} name={"Impact Level"}/>
                <p/>
                <CheckboxSelect options={sdgOptions} value={sdgValue} setValue={setSdgValue} id={"sdgs"} name={"SDGs"}/>
                <p/>
                <CheckboxSelect options={outcomeOptions} value={outcomeValue} setValue={setOutcomeValue} id={"projectOutcomes"} name={"Desired Outcomes"}/>
                <p/>
                <CheckboxSelect options={speciesOptions} value={speciesValue} setValue={setSpeciesValue} id={"species"} name={"Species"}/>
                <p/>
                <CheckboxSelect options={techProviders} value={providerValue} setValue={setProviderValue} id={"providers"} name={"Technology Providers"}/>
                <p/>
                <TextField id="projectWebsite" value={website} onChange={(event) => setWebsite(event.target.value)} label="Project Website" variant="standard" sx={{width: "100%"}} />  
                <p/>

                <Typography component="p" variant="body" color="text.secondary" sx={{pt:5, pb:2}}>
                    Share any documents to feature on your profile:
                </Typography>
                <UrlListEditor links={documents} setLinks={setDocuments} maxItems={5} />


                <Typography component="p" variant="body" color="text.secondary" sx={{pt:5, pb:2}}>
                    When you upload images, they will immediately appear below while they are being uploaded, then they will disappear
                    momentarily as we test the uploaded link, then the re-downloaded image will be shown.  Please wait for the
                    image to re-appear before proceeding.
                </Typography>
                <Typography component="p" variant="body" color="text.secondary" sx={{pb:2}}>
                    Upload a project thumbnail image to be shown in any summary of the project:
                </Typography>
                <ImageGalleryUploader images={thumbnailUrl} setImages={setThumbnailUrl} maxImages={1} onUpload={doThumbUpload}/>

                <Typography component="p" variant="body" color="text.secondary" sx={{pt:5, pb:2}}>
                    Upload your project images to appear on the project page image carousel:
                </Typography>
                <ImageGalleryUploader images={imageUrls} setImages={setImageUrls} maxImages={10} onUpload={doImagesUpload}/>

                <Typography component="p" variant="body" color="text.secondary" sx={{pt:5, pb:2}}>
                By submitting this form, you understand and agree that the information and images you provide may be posted on SAFET's website. 
                You agree that your submission is subject to SAFET's  Privacy Policy and Terms of Use, and you understand that your information 
                and images will be collected, stored, and used as described in those policies. 
                </Typography>

                <Typography component="p" variant="body" color="text.secondary" sx={{pt:0, pb:2}}>
                You confirm that you have the right to share the information and images you submit.
                </Typography>

                { errorText ? <Typography color="error" sx={{mt:2}}>{errorText}</Typography> : <div/> }
                { successText ? <Typography color="green" sx={{mt:2}}>{successText}</Typography> : <div/> }
            </Box>
        </CardContent>
        <CardActions>
            <Button variant="outlined" size="small">Cancel</Button>
            <Button form="theForm" type="submit" variant="contained" size="small">Save</Button>
        </CardActions>
        <p/>
    </Card>
</LocalizationProvider>
</ThemeProvider>
    );
}

// TODO: Obtain these options from the database.

const safetOptions = [
    { label: "Fisheries Management" },
    { label: "Fisheries Enforcement" },
    { label: "Fishing Industry (Commercial)" },
    { label: "Small-scale Fishing" },
    { label: "Government Policy" },
    { label: "Water quality monitoring" },
    { label: "Disease and stress early detection" },
    { label: "Predictive analytics / risk forecasting" },
    { label: "Farm management & productivity" },
    { label: "Sustainability and resilience" },
    { label: "Seafood Buyer" },
    { label: "Scientific Research" },
    { label: "Conservation" },
    { label: "Advocacy" },
    { label: "Other" },
];

const techOptions = [
    { label: "Electronic Monitoring"},
    { label: "Vessel Tracking"},
    { label: "Drones and Uncrewed Vehicles"},
    { label: "Coastal Radar"},
    { label: "Electronic Logbooks"},
    { label: "Traceability Systems"},
    { label: "Surveillance Systems"},
    { label: "Mapping (GIS) Systems"},
    { label: "Artificial Intelligence"},
    { label: "Machine Learning"},
    { label: "Environmental DNA"},
    { label: "In-situ Sensors"},
    { label: "By-catch Reduction Devices"},
    { label: "Aquaculture technology"},
    { label: "Early warning & monitoring systems"},
    { label: "Decision-support tools"},
    { label: "Electric Vessels"},
    { label: "Other"},
];

const stageOptions = [
    { label: "Idea"},
    { label: "Proposal"},
    { label: "Planned"},
    { label: "Funded"},
    { label: "In Progress"},
    { label: "Complete"},
];

const impactOptions = [
    { label: "Unknown"},
    { label: "Estimated"},
    { label: "Partially Measured"},
    { label: "Fully Measured"},
];

const sdgOptions = [
    { label: "1 No Poverty", number: 1},
    { label: "2 Zero Hunger", number: 2},
    { label: "3 Good Health and Wellbeing", number: 3},
    { label: "4 Quality Education", number: 4},
    { label: "5 Gender Equality", number: 5},
    { label: "6 Clean Water and Sanitation", number: 6},
    { label: "7 Affordable and Clean Energy", number: 7},
    { label: "8 Decent Work", number: 8},
    { label: "9 Industry, Innovation, Infrastructure", number: 9},
    { label: "10 Reduced Inequalities", number: 10},
    { label: "11 Sustainable Cities", number: 11},
    { label: "12 Responsible Consumption", number: 12},
    { label: "13 Climate Action", number: 13},
    { label: "14 Life Below Water", number: 14},
    { label: "15 Life On Land", number: 15},
    { label: "16 Peace, Justice, Strong Institutions", number: 16},
    { label: "17 Partnerships for the Goals", number: 17},
];

const outcomeOptions = [
    { label: "MPA Designation"},
    { label: "Sustainable Fishing"},
    { label: "Certification"},
    { label: "Fisheries Improvement"},
    { label: "Food Security"},
    { label: "Reduced Poverty"},
    { label: "Greater Equality"},
    { label: "Improved Livelihoods"},
    { label: "Safeguard Human Rights"},
    { label: "Climate Mitigation"},
    { label: "Climate Adaptation"},
];

const speciesOptions = [
    { label: "Tuna"},
    { label: "Swordfish"},
    { label: "Squid"},
    { label: "Sharks"},
    { label: "Whales"},
    { label: "Dolphins"},
    { label: "Rays"},
    { label: "Freshwater Finfish"},
    { label: "Tilapia"},
    { label: "Catfish"},
];

export default AddProject;